import PropTypes from 'prop-types';
import React from 'react';

import Icon from 'dpl/common/components/Icon';
import Button from 'dpl/common/design_system/Button';
import useCurrentUser from 'dpl/common/hooks/useCurrentUser';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_USER_JOURNEYS,
  ANALYTICS_VIEW_FEATURES
} from 'dpl/constants/analytics';
import {
  ADOPTER_APPLICATION_OPEN,
  LITTER_ID,
  PUPPY_ID
} from 'dpl/constants/query_params';
import { openPuppyPage } from 'dpl/puppy_page/utils/helpers';
import { setAnalyticsOrigin } from 'dpl/shared/utils/setAnalyticsOrigin';
import { sendRudderstackEvent } from 'dpl/util/analytics';
import { addToQuery } from 'dpl/util/queryString';

export default function CardFooter({
  availabilityDescription,
  testimonialsCount,
  breederProfileUrl,
  isReservedPuppy,
  snoozed,
  displayApplyCta,
  analytics,
  puppyId,
  litterId
}) {
  const currentUser = useCurrentUser();

  function handleApplyClick(e) {
    e.preventDefault();
    e.stopPropagation();

    setAnalyticsOrigin({
      originViewFeature: analytics?.view_feature,
      originViewFeatureLocation: analytics?.view_feature_location
    });

    sendRudderstackEvent(ANALYTICS_EVENTS.CARD_CLICKED_BY_USER, {
      ...analytics,
      litter_id: litterId,
      puppy_id: puppyId,
      view_feature: ANALYTICS_VIEW_FEATURES.REQUEST_INFO_BUTTON
    });
    sendRudderstackEvent(ANALYTICS_EVENTS.BUYER_APP_STARTED, {
      ...analytics,
      buyer_profile_id: currentUser?.value?.data?.buyer_profile_id,
      user_journey: ANALYTICS_USER_JOURNEYS.BUYER_ACTIVATION
    });

    const searchParams = addToQuery(
      { [ADOPTER_APPLICATION_OPEN]: true },
      window.location.search
    );
    const [breederProfileEndpoint, hashParams] = breederProfileUrl.split('#');

    const updatedHashParams =
      puppyId && litterId
        ? addToQuery({ [LITTER_ID]: litterId, [PUPPY_ID]: puppyId }, hashParams)
        : hashParams;

    window.location.href = `${breederProfileEndpoint}?${searchParams}${
      updatedHashParams ? `#${updatedHashParams}` : ''
    }`;
  }

  function handleViewInfoClick(e) {
    e.preventDefault();
    e.stopPropagation();

    sendRudderstackEvent(ANALYTICS_EVENTS.CARD_CLICKED_BY_USER, {
      ...analytics,
      litter_id: litterId,
      puppy_id: puppyId,
      view_feature: ANALYTICS_VIEW_FEATURES.VIEW_DETAILS_BUTTON
    });

    if (puppyId) {
      openPuppyPage({ puppyId });
    } else {
      window.location.href = breederProfileUrl;
    }
  }

  const hasVerifiedStories = testimonialsCount > 0;

  return (
    <div className="BreederSearchResultsCardFooter flex-ns justify-between mh4 pv4 items-center f2 bt b--default">
      {(hasVerifiedStories || availabilityDescription) && (
        <div className="flex items-center gap-12 font-14 lh-title">
          {hasVerifiedStories && (
            <div className="flex items-center gap-8" title="Verified stories">
              <Icon
                className="secondary"
                name="fetch-star"
                height="16px"
                width="16px"
              />
              <span className="flex gap-4 lh-1">
                {testimonialsCount}
                <span className="dn db-l">
                  {testimonialsCount > 1
                    ? 'verified stories'
                    : 'verified story'}
                </span>
              </span>
            </div>
          )}
          {hasVerifiedStories && availabilityDescription && (
            <span className="bl b--default" style={{ height: 32 }} />
          )}
          {availabilityDescription && (
            <p className="fw-medium">{availabilityDescription}</p>
          )}
        </div>
      )}
      <CardCTA
        className="ml-auto w-100 w-auto-ns mt3 mt0-md"
        snoozed={snoozed}
        isReservedPuppy={isReservedPuppy}
        displayApplyCta={displayApplyCta}
        handleViewInfoClick={handleViewInfoClick}
        handleApplyClick={handleApplyClick}
      />
    </div>
  );
}

CardFooter.propTypes = {
  availabilityDescription: PropTypes.string,
  testimonialsCount: PropTypes.number,
  breederProfileUrl: PropTypes.string.isRequired,
  snoozed: PropTypes.bool.isRequired,
  isReservedPuppy: PropTypes.bool,
  displayApplyCta: PropTypes.bool.isRequired,
  analytics: PropTypes.shape({
    view: PropTypes.string.isRequired,
    view_feature: PropTypes.string,
    view_feature_location: PropTypes.string
  }),
  puppyId: PropTypes.number,
  litterId: PropTypes.number
};

CardFooter.defaultProps = {
  availabilityDescription: null,
  testimonialsCount: null,
  analytics: {},
  puppyId: null,
  litterId: null,
  isReservedPuppy: false
};

function CardCTA({
  className,
  snoozed,
  isReservedPuppy,
  displayApplyCta,
  handleViewInfoClick,
  handleApplyClick
}) {
  if (snoozed || !displayApplyCta) return null;

  if (isReservedPuppy) {
    return (
      <Button
        data-test-id="breeder_card_view_breeder_link"
        className={className}
        variant="tertiary"
        onClick={handleViewInfoClick}
      >
        View details
      </Button>
    );
  }

  return (
    <Button
      data-test-id="breeder_card_apply_button"
      className={className}
      variant="tertiary"
      onClick={handleApplyClick}
    >
      Request info
    </Button>
  );
}

CardCTA.propTypes = {
  className: PropTypes.string,
  snoozed: PropTypes.bool.isRequired,
  isReservedPuppy: PropTypes.bool.isRequired,
  displayApplyCta: PropTypes.bool.isRequired,
  handleViewInfoClick: PropTypes.func.isRequired,
  handleApplyClick: PropTypes.func.isRequired
};

CardCTA.defaultProps = {
  className: null
};
